import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Text,
  useId,
} from '@coreai/fluentui-react';
import { useNavigate } from 'react-router-dom';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';

interface NodeCodeDiscardConfirmationProps {
  open: boolean;
  destinationDescription: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export const NodeCodeDiscardConfirmation = ({
  open,
  destinationDescription,
  onCancel,
  onConfirm,
}: NodeCodeDiscardConfirmationProps) => {
  const descriptionId = useId('node-code-discard-description-');

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface aria-describedby={descriptionId}>
        <DialogBody>
          <DialogTitle>Discard unapplied code changes?</DialogTitle>
          <DialogContent>
            <Text id={descriptionId}>
              Your JSON changes have not been applied to the workflow. Discard
              them and {destinationDescription}?
            </Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              Keep editing
            </Button>
            <Button appearance="primary" onClick={onConfirm}>
              Discard changes
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const NodeCodeDiscardDialog = () => {
  const pending = useNodeEditorStore((state) => state.pendingNodeCodeDiscard);
  const confirmDiscard = useNodeEditorStore(
    (state) => state.confirmNodeCodeDiscard,
  );
  const cancelDiscard = useNodeEditorStore(
    (state) => state.cancelNodeCodeDiscard,
  );
  const navigate = useNavigate();

  const handleConfirm = async () => {
    const destination = await confirmDiscard();
    if (destination) navigate(destination.to, destination.options);
  };

  const destinationDescription =
    pending?.kind === 'switch'
      ? 'open the selected node'
      : pending?.kind === 'navigate'
        ? 'continue navigating'
        : 'close the editor';

  return (
    <NodeCodeDiscardConfirmation
      open={pending !== null}
      destinationDescription={destinationDescription}
      onCancel={() => void cancelDiscard()}
      onConfirm={() => void handleConfirm()}
    />
  );
};
